import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select, takeEvery } from 'redux-saga/effects';
import {
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { App, areAppTypesEquivalent } from '@ps20-frontend/frontend-models';
import { getAllUwContexts } from '@ps20-frontend/uw';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { VhvCheckoutProdukt, VwgCheckoutProdukt } from '../model';
import { CheckoutAction, CheckoutActions } from '../checkout.actions';
import { shouldUseSdwEndpoint } from '../selector/transient-state.selector';

export const SDS_EXITING_FILES_URL = '/privatschutz/api/sds/files';

export enum FILES_RETRIEVE_EVENTS {
  FILES_RETRIEVE_SUCCESSFUL = 'FILES_RETRIEVE_SUCCESSFUL_EVENT',
  FILES_RETRIEVE_FAILED = 'FILES_RETRIEVE_FAILED',
}

export function* watchFilesAfterLoad(): SagaIterator {
  yield takeEvery(GlobalActions.SAVED_STATES_LOADED, safeSaga(checkFiles, App.SHOP_CHECKOUT));
  yield takeEvery(
    FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_FAILED,
    safeSaga(errorCallback, App.SHOP_CHECKOUT),
  );
}

function getProdukte(produkte: Array<VhvCheckoutProdukt | VwgCheckoutProdukt>, app: App) {
  return produkte.filter(produkt => areAppTypesEquivalent(produkt.appId, app));
}

export function* checkFiles(action: GlobalAction<WrappedSavedStatesLoaded>): SagaIterator {
  const actionPayload: WrappedSavedStates = action.payload.savedStatesLoaded;
  const hasLoadedAppState = actionPayload.some(loadedState => loadedState.appId === App.SHOP_CHECKOUT);
  if (!hasLoadedAppState) {
    return yield cancel();
  }

  const state: IAppState = yield select();
  const produkte = (state.produkte ?? []) as Array<VhvCheckoutProdukt | VwgCheckoutProdukt>;
  const checkoutVhvProdukte = getProdukte(produkte, App.VHV);
  const checkoutVwgProdukte = getProdukte(produkte, App.VWG);
  if (
    (!checkoutVhvProdukte || checkoutVhvProdukte.length === 0) &&
    (!checkoutVwgProdukte || checkoutVwgProdukte.length === 0)
  ) {
    return yield cancel();
  }
  let filesToDownload: string[] = [];

  checkoutVhvProdukte?.forEach(
    checkoutProdukt =>
      (filesToDownload = filesToDownload.concat(
        getVhvDocumentsIds(checkoutProdukt as VhvCheckoutProdukt),
      )),
  );

  const registeredContextKeys = new Set(getAllUwContexts().map(descriptor => descriptor.key));

  checkoutVwgProdukte?.forEach(checkoutProdukt => {
    const uwEntries = (checkoutProdukt as VwgCheckoutProdukt)?.uw?.entries ?? [];
    uwEntries
      .filter(entry => registeredContextKeys.has(entry.contextKey))
      .forEach(entry => {
        filesToDownload = filesToDownload.concat(entry.files.flatMap(file => file.uploadId));
      });
  });

  if (filesToDownload.length === 0) {
    return yield cancel();
  }

  yield call(callBackend, filesToDownload);
}

export function* callBackend(sdsFileRequest: string[]): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      method: HTTP_METHOD.GET,
      appId: App.SHOP_CHECKOUT,
      url: getEndpointUrl(shouldUseSdwEndpoint(state), SDS_EXITING_FILES_URL + '/' + sdsFileRequest),
      successEvent: FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_SUCCESSFUL,
      errorEvent: FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_FAILED,
    }),
  );
}

export function* successCallback(successAction: CheckoutAction<Array<string>>): SagaIterator {
  const resp: Array<string> = successAction.payload;
  yield put(new CheckoutActions().detectNotExistingFiles(resp));
}

export function* errorCallback(): SagaIterator {
  yield call(
    [PS20Logger, PS20Logger.error],
    App.SHOP_CHECKOUT,
    'SDS file existence lookup failed; keeping the stored file references untouched',
  );
}

export function getVhvDocumentsIds(vhvCheckoutProdukt: VhvCheckoutProdukt): Array<string> {
  let result: string[] = [];
  result = result.concat(
    vhvCheckoutProdukt?.einbruchMeldeanlage?.files?.map(file => file.uploadId) ?? [],
  );
  result = result.concat(
    vhvCheckoutProdukt?.mechanischeSicherungen?.files?.map(file => file.uploadId) ?? [],
  );
  vhvCheckoutProdukt?.tresor?.eintraege?.forEach(
    eintrag => (result = result.concat(eintrag.files?.map(file => file.uploadId) ?? [])),
  );
  return result;
}
